import type { LensDatasetField } from '~/types/leLens';

import {
  FieldExpressionError,
  type FieldNode,
  evaluateFieldExpression,
  fieldExpressionColumns,
  parseFieldExpression,
} from './fieldExpression';
import { fieldRollupFault } from './ratioGuard';

/** A field that parsed, with what it reads already worked out. */
export type ReadyField = { field: LensDatasetField; node: FieldNode; reads: Array<string> };

export type FieldFault = { name: string; reason: string };

export type PreparedFields = {
  /** Evaluated before grouping, in an order where each can see the ones before it. */
  row: Array<ReadyField>;
  /** Evaluated after grouping, in the same dependency order. */
  aggregate: Array<ReadyField>;
  /** Fields left out, and why, so a reader is told rather than shown a blank. */
  faults: Array<FieldFault>;
};

/**
 * Checks a dataset's calculated fields and puts them in an order they can be
 * evaluated in.
 *
 * A field is dropped rather than half-applied: one that names a column nothing
 * returns, or that depends on itself, would render as null in every row, which
 * reads as "no data" rather than as "this formula is wrong".
 */
export function prepareFields(
  fields: ReadonlyArray<LensDatasetField> | undefined,
  columns: ReadonlyArray<string>,
): PreparedFields {
  const faults: Array<FieldFault> = [];
  if (!fields?.length) {
    return { aggregate: [], faults, row: [] };
  }

  const parsed = new Map<string, ReadyField>();
  const seen = new Set<string>();

  for (const field of fields) {
    const name = field.name?.trim();
    // A row with nothing in it is a reader who has just pressed Add, not a
    // mistake. It is left out of both the fields and the faults, so the editor
    // does not open with an error against an untouched line.
    if (!name && !field.expression?.trim()) {
      continue;
    }
    if (!name) {
      faults.push({ name: '', reason: 'A field needs a name.' });
      continue;
    }
    if (columns.includes(name)) {
      faults.push({ name, reason: 'The query already returns a column with this name.' });
      continue;
    }
    if (seen.has(name)) {
      faults.push({ name, reason: 'Two fields on this dataset have this name.' });
      continue;
    }
    seen.add(name);

    try {
      const node = parseFieldExpression(field.expression ?? '');
      parsed.set(name, { field: { ...field, name }, node, reads: fieldExpressionColumns(node) });
    } catch (error) {
      faults.push({
        name,
        reason: error instanceof FieldExpressionError ? error.message : 'This formula could not be read.',
      });
    }
  }

  const resolvable = new Set(columns);
  const ordered: Array<ReadyField> = [];
  let remaining = [...parsed.values()];

  // Repeated passes rather than a graph walk: a field can read another field,
  // and the number of fields on one dataset is small enough that clarity is
  // worth more here than the asymptotics.
  for (;;) {
    // A field may read another field on the same dataset, which resolves on a
    // later pass once that one has been ordered.
    const ready = remaining.filter((entry) => entry.reads.every((column) => resolvable.has(column)));
    if (!ready.length) {
      break;
    }
    ready.forEach((entry) => {
      ordered.push(entry);
      resolvable.add(entry.field.name);
    });
    remaining = remaining.filter((entry) => !ready.includes(entry));
  }

  for (const entry of remaining) {
    const unknown = entry.reads.filter((column) => !resolvable.has(column));
    faults.push({
      name: entry.field.name,
      reason: unknown.some((column) => parsed.has(column))
        ? `This formula depends on itself, through ${unknown.filter((column) => parsed.has(column)).join(', ')}.`
        : `This dataset does not return ${unknown.join(', ')}.`,
    });
  }

  // An aggregate field may read a row field, never the other way round: a row
  // is evaluated before there are any groups to total.
  const row = ordered.filter((entry) => entry.field.when !== 'aggregate');
  const aggregate = ordered.filter((entry) => entry.field.when === 'aggregate');
  const aggregateNames = new Set(aggregate.map((entry) => entry.field.name));
  const kept: Array<ReadyField> = [];

  for (const entry of row) {
    const bad = entry.reads.filter((column) => aggregateNames.has(column));
    if (bad.length) {
      faults.push({
        name: entry.field.name,
        reason: `A row field cannot read ${bad.join(', ')}, which is only known once rows are grouped.`,
      });
      continue;
    }
    kept.push(entry);
  }

  // A rate is only correct when it divides two totals. A field that would be
  // averaged instead is dropped rather than shown, with the two columns to use
  // named: the wrong number here looks exactly like the right one.
  const rollupSafe = (entries: Array<ReadyField>) =>
    entries.filter((entry) => {
      const fault = fieldRollupFault(entry.field, entry.node, columns);
      if (fault) {
        faults.push({ name: entry.field.name, reason: fault.message });
        return false;
      }
      return true;
    });

  return { aggregate: rollupSafe(aggregate), faults, row: rollupSafe(kept) };
}

/** Adds the row-level fields to each row, in order, so a later field sees an
 *  earlier one. */
export function applyRowFields(
  rows: Array<Record<string, unknown>>,
  fields: ReadonlyArray<ReadyField>,
): Array<Record<string, unknown>> {
  if (!fields.length) {
    return rows;
  }
  return rows.map((row) => {
    const next = { ...row };
    for (const entry of fields) {
      next[entry.field.name] = evaluateFieldExpression(entry.node, next);
    }
    return next;
  });
}

/**
 * Adds the aggregate fields to rows that already hold their group's totals.
 *
 * This is where a rate becomes correct: the row handed in carries sum(margin)
 * and sum(ttv), so `gross_margin_aud / ttv_aud` divides the totals.
 */
export function applyAggregateFields(
  rows: Array<Record<string, unknown>>,
  fields: ReadonlyArray<ReadyField>,
  columns: ReadonlyArray<string> = [],
): Array<Record<string, unknown>> {
  // Checked again here, not only where the field was defined. A spec saved
  // before this check existed still reaches this function, and an average of
  // rates is not something a reader can see is wrong.
  return applyRowFields(
    rows,
    fields.filter((entry) => !fieldRollupFault(entry.field, entry.node, columns)),
  );
}

/** Every column an aggregate field needs to have survived grouping. */
export function aggregateFieldInputs(fields: ReadonlyArray<ReadyField>): Array<string> {
  const names = new Set(fields.map((entry) => entry.field.name));
  return [...new Set(fields.flatMap((entry) => entry.reads).filter((column) => !names.has(column)))];
}
